import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Avatar, Icon, Stack, Text } from '@/design-system'
import { BRAND } from '@/brand/brand'
import { useAuth } from '@/app/auth/auth-context'
import { cn } from '@/lib/cn'
import styles from './UserMenu.module.css'

export function UserMenu({ onNavigate }: { readonly onNavigate?: (() => void) | undefined }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const go = (to: string) => {
    setOpen(false)
    onNavigate?.()
    navigate(to)
  }

  const name = user ? `${user.firstName} ${user.lastName}` : BRAND.name

  return (
    <div className={styles.wrap} ref={wrapRef}>
      {open && (
        <div className={styles.menu} role="menu">
          <button type="button" role="menuitem" className={styles.item} onClick={() => go('/profile')}>
            <Icon name="settings" size={18} />
            Profile & security
          </button>
          <button type="button" role="menuitem" className={styles.item} onClick={() => go('/plans')}>
            <Icon name="workspace_premium" size={18} />
            Your plan
          </button>
          <button type="button" role="menuitem" className={styles.item} onClick={() => go('/support')}>
            <Icon name="support_agent" size={18} />
            Support
          </button>
          <span className={styles.divider} />
          <button
            type="button"
            role="menuitem"
            className={cn(styles.item, styles.danger)}
            onClick={() => {
              setOpen(false)
              logout()
              navigate('/login', { replace: true })
            }}
          >
            <Icon name="logout" size={18} />
            Log out
          </button>
        </div>
      )}

      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={name} size={36} />
        <Stack align="start" grow gap={0}>
          <Text variant="caption" weight="semibold" truncate>
            {name}
          </Text>
          <Text variant="eyebrow" weight="semibold" tone="positive">
            {user ? `Tier ${user.tier} · verified` : ''}
          </Text>
        </Stack>
        <Icon name="unfold_more" size={16} />
      </button>
    </div>
  )
}
